import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
    };
}

export interface GAIAHerbsFarmMiniBannerStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    buttonWrapper?: ButtonProps;
}

export const gaiaHerbsFarmMiniBannerStyles: GAIAHerbsFarmMiniBannerStyles = {
    mainGridContainer: {
        gap: 0,
        css: css`
            width: 100%;
            display: flex;
            background-color: #183028;
            opacity: 0.9;
            padding: 72px 64px 59px;
            min-height: 402px;
            border-radius: 10px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 40px 20px;
                        min-height: auto;
                    `,
                    css`
                        padding: 40px 20px;
                        min-height: auto;
                    `,
                    css`
                        padding: 40px 40px;
                        min-height: auto;
                    `,
                    css``,
                    css`
                        margin: 40px 0 !important;
                    `,
                ])}
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            padding: 0;
        `,
    },
    titleLabel: {
        variant: "h2",
        css: css`
            color: #ffffff;
            font-size: 2rem;
            margin-bottom: 55px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 20px;
                    `,
                    css`
                        margin-bottom: 20px;
                    `,
                    css`
                        margin-bottom: 30px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    descriptionText: {
        css: css`
            > p {
                color: #ffffff;
                margin-bottom: 60px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > p {
                            margin-bottom: 20px;
                        }
                    `,
                    css`
                        > p {
                            margin-bottom: 20px;
                        }
                    `,
                    css`
                        > p {
                            margin-bottom: 30px;
                        }
                    `,
                    css``,
                    css``,
                ])}
        `,
    },

    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            padding: 0;
        `,
    },
    buttonWrapper: {
        css: css`
            border-radius: 10px;
            padding: 0 40px;
        `,
    },
};

const styles = gaiaHerbsFarmMiniBannerStyles;

type Props = OwnProps;

export const GAIAHerbsFarmMiniBanner = ({ fields }: Props) => {
    const { url } = useGetLink(fields.buttonLink);
    const onClick = () => {
        if (!url) {
            return;
        }
        window.location.href = url;
    };

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleLabel}>{fields.heading}</Typography>
            </GridItem>
            <GridItem {...styles.descriptionGridItem}>
                <Typography {...styles.descriptionText}>{parse(fields.subheading, parserOptions)}</Typography>
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Button
                    {...styles.buttonWrapper}
                    aria-label={`${fields.buttonLabel} about ${fields.heading}`}
                    onClick={onClick}
                >
                    {fields.buttonLabel}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: GAIAHerbsFarmMiniBanner,
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "TextField",
                defaultValue: "The Gaia Herbs Farm",
                fieldType: "Translatable",
            },
            {
                name: fields.subheading,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue:
                    "<p>No other herbal manufacturer puts more funding back into research and development of the future of herbal medicine and organic farming.</p>",
                fieldType: "Translatable",
            },

            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "Learn More",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
